import { useQuery } from '@tanstack/react-query';
import { api } from '@/app/api';
import type { Metrics, MatterRow, PipelineSummary } from '@/app/types';

/** Rows and counts, refetched when the tab regains focus (the presenter moves matters in another tab). */
export function usePipelineRows() {
  return useQuery({ queryKey: ['matters'], queryFn: () => api<MatterRow[]>('/api/matters'), staleTime: 15_000 });
}

export function usePipelineSummary() {
  return useQuery({ queryKey: ['pipeline', 'summary'], queryFn: () => api<PipelineSummary>('/api/pipeline/summary'), staleTime: 15_000 });
}

/** Counts derived from the rows when the summary is unavailable (docs/ux/pipeline.md, degraded states). */
export function deriveCounts(rows: MatterRow[]): PipelineSummary['byStatus'] {
  const out: PipelineSummary['byStatus'] = {};
  for (let s = 1; s <= 15; s += 1) out[String(s)] = { count: 0, overdue: 0 };
  for (const r of rows) {
    const cell = out[String(r.status)]!;
    cell.count += 1;
    if (r.slaState === 'overdue') cell.overdue += 1;
  }
  return out;
}

/** The metrics tab's fold (docs/ux/metrics.md); thirty audit reads on the server, so it is cached a little longer than the board. */
export function useMetrics(enabled: boolean) {
  return useQuery({ queryKey: ['pipeline', 'metrics'], queryFn: () => api<Metrics>('/api/pipeline/metrics'), staleTime: 30_000, retry: (count) => count < 1, enabled });
}

